// Core imports
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Skeleton, Typography } from 'antd';
import { CalendarOutlined, CheckOutlined, LinkOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO/SEO';
import { FacebookIcon } from '../components/Icons/FacebookIcon';
import { XIcon } from '../components/Icons/XIcon';
import { LinkedinIcon } from '../components/Icons/LinkedinIcon';
import { WebsiteIcon } from '../components/Icons/WebsiteIcon';

// Misc imports
import api from '../api';
import { NAV_BLOG, NAV_BLOG_AUTHOR } from '../constants';
import NewsletterSignup from '../components/Misc/NewsletterSignup';
import SupportPrompt from '../components/Misc/SupportPrompt';

const { Title, Paragraph } = Typography;

const BlogAuthorMini = ({ author }) => {
  if (!author) {
    return null;
  }

  const initials = author.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const authorHref = NAV_BLOG_AUTHOR.replace(':slug', author.slug);

  return (
    <Link to={authorHref} className="blog-post-author-link">
      {author.pictureUrl ? (
        <img src={author.pictureUrl} alt={author.name} className="blog-author-avatar" />
      ) : (
        <div className="blog-author-avatar-placeholder">{initials}</div>
      )}
      <span className="blog-post-author-name">{author.name}</span>
    </Link>
  );
};

BlogAuthorMini.propTypes = {
  author: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    pictureUrl: PropTypes.string
  })
};

const BlogPostScreen = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.fetchBlogPost({ slug })
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="landing-container blog-post-page" style={{ paddingTop: 48, paddingBottom: 80 }}>
        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 12 }} />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="landing-container" style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <Title level={2}>Post not found</Title>
        <Paragraph>
          <Link to={NAV_BLOG}>← Back to blog</Link>
        </Paragraph>
      </div>
    );
  }

  const publishedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const authorHref = post.author ? NAV_BLOG_AUTHOR.replace(':slug', post.author.slug) : '#';
  const authorInitials = post.author?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  const safeContent = DOMPurify.sanitize(post.content || '');

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://ndearchive.com';
  const postUrl = `${siteUrl}/blog/${post.slug}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(postUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt || `Read "${post.title}" on NDE Archive Blog.`}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.coverImageUrl || undefined}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          image: post.coverImageUrl ? [post.coverImageUrl] : undefined,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt,
          author: post.author ? {
            '@type': 'Person',
            name: post.author.name,
            url: `${siteUrl}/blog/author/${post.author.slug}`
          } : undefined,
          publisher: {
            '@type': 'Organization',
            name: 'NDE Archive',
            url: siteUrl
          }
        }}
      />

      <div className="landing-container blog-post-page">
        {/* Sticky share bar */}
        <div className="blog-post-share-sticky">
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(post.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="blog-share-btn"
            aria-label="Share on X (Twitter)"
          >
            <XIcon />
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="blog-share-btn"
            aria-label="Share on Facebook"
          >
            <FacebookIcon />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="blog-share-btn"
            aria-label="Share on LinkedIn"
          >
            <LinkedinIcon />
          </a>
          <button
            onClick={handleCopyLink}
            className={`blog-share-btn${copied ? ' blog-share-btn--copied' : ''}`}
            aria-label="Copy link"
          >
            {copied ? <CheckOutlined /> : <LinkOutlined />}
          </button>
        </div>

        <Link to={NAV_BLOG} className="blog-post-back">← Back to Blog</Link>

        {/* Cover image */}
        {post.coverImageUrl && (
          <img src={post.coverImageUrl} alt={post.title} className="blog-post-cover" />
        )}

        {/* Header */}
        <div className="blog-post-header">
          {post.tags?.length > 0 && (
            <div className="blog-post-tags">
              {post.tags.map(tag => (
                <span
                  key={tag.id}
                  className="blog-tag-badge"
                  onClick={() => navigate(`${NAV_BLOG}?tag=${tag.slug}`)}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          <h1 className="blog-post-title">{post.title}</h1>

          <div className="blog-post-meta">
            <Link to={authorHref} className="blog-post-author-link">
              {post.author?.pictureUrl ? (
                <img src={post.author.pictureUrl} alt={post.author.name} className="blog-author-avatar" />
              ) : (
                <div className="blog-author-avatar-placeholder">{authorInitials}</div>
              )}
              <span className="blog-post-author-name">{post.author?.name}</span>
            </Link>

            {publishedDate && (
              <>
                <span className="blog-post-meta-sep">·</span>
                <span className="blog-post-date">
                  <CalendarOutlined className="mr-4" />
                  {publishedDate}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="blog-post-content-wrap">
          <div
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: safeContent }}
          />

          {/* Author card */}
          {post.author && (
            <div
              className="blog-post-author-card"
              style={{
                background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.08), rgba(191, 193, 255, 0.08))',
                border: 'none'
              }}
            >
              <Link to={authorHref}>
                {post.author.pictureUrl ? (
                  <img src={post.author.pictureUrl} alt={post.author.name} className="blog-author-avatar-lg" />
                ) : (
                  <div className="blog-author-avatar-placeholder-lg">{authorInitials}</div>
                )}
              </Link>
              <div className="blog-post-author-card-info">
                <Title level={5} style={{ marginBottom: 4 }}>
                  <Link to={authorHref}>{post.author.name}</Link>
                </Title>
                {post.author.bio && (
                  <Paragraph className="text-color-black-80 mb-12" style={{ fontSize: 14 }}>
                    {post.author.bio.split('\n')[0]}
                  </Paragraph>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  {post.author.linkedinUrl && (
                    <a href={post.author.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      <LinkedinIcon style={{ fontSize: 18, color: '#9195fc' }} />
                    </a>
                  )}
                  {post.author.facebookUrl && (
                    <a href={post.author.facebookUrl} target="_blank" rel="noopener noreferrer">
                      <FacebookIcon style={{ fontSize: 18, color: '#9195fc' }} />
                    </a>
                  )}
                  {post.author.xUrl && (
                    <a href={post.author.xUrl} target="_blank" rel="noopener noreferrer">
                      <XIcon style={{ fontSize: 18, color: '#9195fc' }} />
                    </a>
                  )}
                  {post.author.websiteUrl && (
                    <a href={post.author.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <WebsiteIcon style={{ fontSize: 18, color: '#9195fc' }} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <SupportPrompt variant="blog" />

        <NewsletterSignup />
      </div>
    </>
  );
};

export default BlogPostScreen;
